import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, LOGO, BRAND } from "@/lib/config";
import { CheckCircle2, Loader2, XCircle, Phone } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | failed

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setStatus("failed"); return; }
      } catch (e) {
        console.debug("Payment status not ready yet, will retry:", e?.message || e);
      }
      attempts += 1;
      if (attempts < 8) setTimeout(poll, 2000);
      else setStatus("failed");
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-boco-navy flex items-center justify-center px-5">
      <div className="max-w-md w-full rounded-3xl bg-white p-10 text-center shadow-2xl">
        <img src={LOGO} alt="BOCO BINS" className="mx-auto h-16 w-16 rounded-full object-cover" />
        {status === "checking" && (
          <div className="mt-6" data-testid="payment-checking">
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-boco-navy" />
            <h1 className="mt-4 font-display text-2xl font-black text-boco-navy">Confirming your payment...</h1>
            <p className="mt-2 text-slate-600">Hang tight, this only takes a moment.</p>
          </div>
        )}
        {status === "paid" && (
          <div className="mt-6" data-testid="payment-success">
            <CheckCircle2 className="mx-auto h-14 w-14 text-boco-green" />
            <h1 className="mt-4 font-display text-2xl font-black text-boco-navy">Booking Confirmed!</h1>
            <p className="mt-2 text-slate-600">Thanks for choosing BOCO BINS. We've emailed your confirmation and will reach out to schedule your cleaning.</p>
            <Link to="/" className="mt-6 inline-flex rounded-full bg-boco-green px-7 py-3 font-bold text-boco-navy hover:bg-boco-greendark hover:text-white transition-colors" data-testid="success-home-btn">Back to Home</Link>
          </div>
        )}
        {status === "failed" && (
          <div className="mt-6" data-testid="payment-failed">
            <XCircle className="mx-auto h-14 w-14 text-amber-500" />
            <h1 className="mt-4 font-display text-2xl font-black text-boco-navy">We couldn't confirm payment</h1>
            <p className="mt-2 text-slate-600">If you were charged, don't worry — call us and we'll sort it out right away.</p>
            <a href={BRAND.phoneHref} className="mt-6 inline-flex items-center gap-2 rounded-full bg-boco-navy px-7 py-3 font-bold text-white hover:bg-boco-navylight transition-colors"><Phone className="h-4 w-4" /> {BRAND.phone}</a>
            <div className="mt-4"><Link to="/book" className="text-sm text-slate-500 hover:text-boco-navy">Try booking again</Link></div>
          </div>
        )}
      </div>
    </div>
  );
}
